import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { Divider, Input, message, Upload, Progress, Button } from 'antd';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { TranslateButton } from 'views/Conversation/Chat/components/MessageEditor/MessageEditorFooter/TranslateButton';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useCurrentChatBuyerLang } from 'hooks/business/im/chatPreference';
import { getTranslateContent } from '../utils';
import {
  SupplementKnowledgeAnswer,
  SupplementKnowledgeDetailResponse,
  UpdateSupplementKnowledgeV2Body,
  updateSupplementKnowledgeV2,
} from 'services/api/supplementKnowledge';
import { RewriteQuestionPayload, hasRewriteQuestionDraft } from '../context/rewriteQuestionDraft';
import { ImageIcon, VideoIcon } from 'views/Conversation/Chat/components/MessageEditor/ToolbarIcon';
import validatorVideo from 'utils/validator/video';
import { uploadResource } from 'services/api/uploads';
import SafeImage from 'components/SafeImage';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { VideoPreview } from 'components/VideoPreview';
import { useMutation } from '@tanstack/react-query';
import { toUpperCase } from 'utils/toLowerCase';
import wait from 'utils/wait';
import { random } from 'lodash-es';
import { uploadFileV2 } from 'services/api/resource';
import { useMemoizedFn } from 'ahooks';
import isEqual from 'react-fast-compare';

interface Props {
  region: Conversation.Conversation['region'];
  channel: Conversation.Conversation['channel'];
  storeId?: string;
  enable?: boolean;
  dataSource: SupplementKnowledgeDetailResponse;
  onActionDone?: () => void;
  rewriteQuestionPayload?: RewriteQuestionPayload;
  render?: (
    editor: ReactNode,
    props: {
      answers: SupplementKnowledgeAnswer[];
      submitEnable: boolean;
    },
  ) => JSX.Element;
}

// 扩展文件状态类型，支持上传状态管理
interface FileItem extends SupplementKnowledgeAnswer {
  uid: string;
  status?: 'uploading' | 'done' | 'error';
  percent?: number;
  originFileObj?: File;
}

// 扩展 File 类型以包含 uid
interface FileWithUid extends File {
  uid: string;
}

export const PureAnswerEditor = ({
  citeMode,
  region,
  channel,
  storeId,
  answers,
  onAnswersChange,
  autoFocus = false,
}: Pick<Props, 'region' | 'channel'> & {
  citeMode?: boolean;
  storeId?: string;
  answers: SupplementKnowledgeAnswer[];
  onAnswersChange: (answers: SupplementKnowledgeAnswer[]) => void;
  autoFocus?: boolean;
}) => {
  const [textAnswer] = useState(() => answers?.find((answer) => answer.msgType === 'TEXT'));

  const language = useSystemLanguageValue();
  const defaultTargetLang = useCurrentChatBuyerLang(region, channel);
  const [targetLang, setTargetLang] = useState(defaultTargetLang);

  const aTranslate = getTranslateContent(language, textAnswer?.translateMap);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState(
    aTranslate || (citeMode ? textAnswer?.message || '' : ''),
  );
  const [translating, setTranslating] = useState(false);
  const { t } = useTranslate();

  const [fileList, setFileList] = useState<FileItem[]>(() =>
    answers
      .filter((answer) => answer.msgType === 'IMAGE' || answer.msgType === 'VIDEO')
      .map((answer) => ({
        ...answer,
        uid: answer.messageId || uuidv4(),
        status: 'done' as const,
      })),
  );

  // 更新文件状态
  const updateFileStatus = (uid: string, updates: Partial<FileItem>) => {
    setFileList((prev) => prev.map((file) => (file.uid === uid ? { ...file, ...updates } : file)));
  };

  // 删除文件
  const removeFile = (uid: string) => {
    setFileList((prev) => prev.filter((file) => file.uid !== uid));
  };

  const handleTranslateClick = async () => {
    try {
      setTranslating(true);
      await overseaTranslate(inputValue, targetLang, 'supplementKnowledge', region, channel).then(
        (resp) => {
          setInputValue(resp as unknown as string);
        },
      );
    } catch (err) {
      message.error(describeKnowledgeApiError(err, undefined, t('Entities.翻译失败')));
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => {
    setInputValue(aTranslate ?? (citeMode ? textAnswer?.message || '' : ''));
  }, [aTranslate, textAnswer, citeMode]);

  useLayoutEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  const memoizedChange = useMemoizedFn(onAnswersChange);

  useEffect(() => {
    memoizedChange([
      {
        ...textAnswer,
        msgType: 'TEXT',
        message: inputValue,
      },
      ...fileList
        .filter((_) => _.status === 'done')
        .map((file) => ({
          message: file.localUrl,
          msgType: file.msgType,
          url: file.url,
          localUrl: file.localUrl,
          thumbUrl: file.thumbUrl,
        })),
    ]);
  }, [textAnswer, inputValue, fileList, memoizedChange]);

  return (
    <>
      <div className="p-2 border border-solid border-[var(--fett-fill-300)] rounded-sm flex flex-col">
        <Input.TextArea
          ref={textareaRef}
          className="!m-0 !p-0 !text-xs !text-[var(--fett-text-primary)]"
          bordered={false}
          value={inputValue}
          rows={6}
          onChange={(e) => setInputValue(e.target.value)}
        />
        {fileList.length > 0 && (
          <>
            <Divider className="!my-2" type="horizontal" />
            <div className="grid grid-cols-3 gap-1 w-full">
              {fileList.map((file) => {
                const fileName = file.originFileObj?.name || file.localUrl?.split('/').pop() || '';
                return (
                  <div
                    className="w-full cursor-pointer flex items-center justify-between gap-1 p-1 hover:bg-[#F5F5F5] border border-solid border-[var(--fett-fill-300)] rounded-sm relative"
                    key={file.uid}
                  >
                    <div className="flex-1 relative flex items-center gap-1 overflow-hidden">
                      {file.status === 'uploading' ? (
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                          <LoadingOutlined className="text-gray-400" />
                        </div>
                      ) : file.status === 'error' ? (
                        <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                          <span className="text-red-500 text-xs">×</span>
                        </div>
                      ) : file.msgType === 'IMAGE' ? (
                        <SafeImage
                          preview
                          width={32}
                          height={32}
                          src={file.thumbUrl || file.localUrl || file.url}
                        />
                      ) : file.msgType === 'VIDEO' ? (
                        <VideoPreview
                          videoUrl={file.localUrl || file.url || ''}
                          thumbUrl={file.thumbUrl}
                          thumbWidth={32}
                          thumbHeight={32}
                        />
                      ) : null}
                      {file.status === 'uploading' && file.percent !== undefined ? (
                        <Progress
                          percent={file.percent}
                          size="small"
                          showInfo={false}
                          strokeColor="#1890ff"
                        />
                      ) : (
                        <div
                          className="flex-1 text-ellipsis whitespace-nowrap overflow-hidden text-gray-400 text-xs"
                          title={fileName}
                        >
                          {fileName}
                        </div>
                      )}
                    </div>
                    <DeleteOutlined
                      className="text-gray-400 hover:text-red-500"
                      onClick={() => removeFile(file.uid)}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
        <div className="flex items-center justify-between gap-2 mt-4">
          <div className="flex items-center gap-2">
            <Upload
              name="file"
              showUploadList={false}
              accept={['jpg', 'jpeg', 'gif', 'png'].map((_) => `.${_}`).join(',')}
              beforeUpload={(file) => {
                const sizeLimit = 10 * 1024 * 1024;
                if (file.size > sizeLimit) {
                  message.warn(
                    t('upload.limitWarn', {
                      size: `${(sizeLimit / 1024 / 1024).toFixed(2)}M`,
                    }),
                  );
                  return Upload.LIST_IGNORE;
                }

                // 添加文件到列表，状态为上传中
                const fileItem: FileItem = {
                  uid: file.uid,
                  messageId: file.uid,
                  localUrl: file.name,
                  msgType: 'IMAGE',
                  status: 'uploading',
                  percent: random(10, 80),
                  originFileObj: file,
                };

                setFileList((prev) => [...prev, fileItem]);
              }}
              customRequest={({ file, onSuccess, onError }) => {
                const fileObj = file as FileWithUid;

                if (!storeId) {
                  message.error('ViewsChat.上传失败');
                  return;
                }

                uploadResource({
                  file: fileObj,
                  storeId,
                  region,
                  fileType: 'IMAGE',
                  reqSource: 'UPDATE_SUPPLEMENT_KNOWLEDGE',
                })
                  .then((res) => {
                    const { url, localUrl } = res;
                    // 更新文件状态为成功，并设置 URL
                    updateFileStatus(fileObj.uid, {
                      status: 'done',
                      url: url || localUrl,
                      localUrl: localUrl || url,
                      thumbUrl: localUrl,
                      percent: 100,
                    });
                    onSuccess?.(res);
                  })
                  .catch((error) => {
                    // 更新文件状态为失败
                    updateFileStatus(fileObj.uid, {
                      status: 'error',
                      percent: 0,
                    });
                    onError?.(error);
                    message.error('图片上传失败');
                  });
              }}
            >
              <ImageIcon className="cursor-pointer" />
            </Upload>
            <Upload
              name="file"
              showUploadList={false}
              accept=".mp4"
              beforeUpload={(file) => {
                return validatorVideo(toUpperCase(channel), file, [0, 180])
                  .catch(() => ({ errorText: '' }))
                  .then(({ errorText }) => {
                    if (errorText) {
                      message.warn(errorText);
                      return Upload.LIST_IGNORE;
                    }

                    // 添加视频文件到列表，状态为上传中
                    const fileItem: FileItem = {
                      uid: file.uid,
                      messageId: file.uid,
                      localUrl: file.name,
                      msgType: 'VIDEO',
                      status: 'uploading',
                      percent: random(10, 80),
                      originFileObj: file,
                    };

                    setFileList((prev) => [...prev, fileItem]);
                  });
              }}
              customRequest={({ file, onSuccess, onError }) => {
                const fileObj = file as FileWithUid;
                if (!storeId) {
                  message.error('ViewsChat.上传失败');
                  return;
                }

                uploadFileV2({ storeId, region, file: fileObj })
                  .then((res) => {
                    const { videoUrl, localVideoUrl, thumbUrl, durationSeconds } = res;
                    // 更新视频文件状态为成功，并设置 URL
                    updateFileStatus(fileObj.uid, {
                      status: 'done',
                      url: videoUrl || localVideoUrl,
                      localUrl: localVideoUrl || videoUrl,
                      thumbUrl,
                      durationSeconds: durationSeconds?.toString(),
                      percent: 100,
                    });
                    onSuccess?.(res);
                  })
                  .catch((error) => {
                    // 更新文件状态为失败
                    updateFileStatus(fileObj.uid, {
                      status: 'error',
                      percent: 0,
                    });
                    onError?.(error);
                    message.error('视频上传失败');
                  });
              }}
            >
              <VideoIcon className="cursor-pointer" />
            </Upload>
          </div>
          <TranslateButton
            region={region}
            channel={channel}
            controlStatus={true}
            targetLang={targetLang}
            onChange={setTargetLang}
            translating={translating}
            trimInput={inputValue.trim()}
            onTranslateClick={handleTranslateClick}
            size="small"
          />
        </div>
      </div>
    </>
  );
};

export const AnswerEditor = (props: Props) => {
  const { region, channel, storeId, enable, dataSource, onActionDone, rewriteQuestionPayload } =
    props;
  const { id, supplementalKnowledgeValue } = dataSource;
  const { answer } = supplementalKnowledgeValue;

  const [answers, setAnswers] = useState<SupplementKnowledgeAnswer[]>([]);

  const [editKey, setEditKey] = useState<number>(0);

  const fileAnswers = answers.filter((_) => _.msgType !== 'TEXT');
  const textAnswer = answers.find((answer) => answer.msgType === 'TEXT');
  const inputValue = textAnswer?.message;

  const language = useSystemLanguageValue();

  const aTranslate = getTranslateContent(language, textAnswer?.translateMap);

  const { t } = useTranslate();

  const { mutateAsync: updateSupplementKnowledgeMutation, isLoading } = useMutation({
    mutationFn: async (body: UpdateSupplementKnowledgeV2Body) => {
      const response = await updateSupplementKnowledgeV2(body);
      // 后端翻译有延迟, 延迟一下
      await wait(random(2500, 5000));
      return response;
    },
    onSuccess: () => {
      onActionDone?.();
    },
    onError: (knowledgeError) => {
      message.error(
        describeKnowledgeApiError(knowledgeError, undefined, t('translation.操作失败，请稍后再试')),
      );
    },
  });

  const handleSaveClick = () => {
    updateSupplementKnowledgeMutation({
      id,
      supplementalKnowledgeValue: {
        rewriteAnswers: answers,
        ...rewriteQuestionPayload,
      },
      manualReviewStatus: 'REVIEW_PASSED',
    });
  };

  const mergeAnswers = (answers: SupplementKnowledgeAnswer[]) => {
    const result = answers.reduce(
      (acc, answer) => {
        if (answer.msgType === 'TEXT') {
          const prevMessage = acc.textAnswer.message;
          acc.textAnswer.message = prevMessage
            ? prevMessage + '\n' + answer.message
            : answer.message;
          acc.textAnswer.messageId = answer.messageId || '';
          if (answer.translateMap) {
            Object.entries(answer.translateMap).forEach(([key, value]) => {
              const prevTranslateContent = acc.textAnswer.translateMap?.[key];
              acc.textAnswer.translateMap = {
                ...acc.textAnswer.translateMap,
                [key]: prevTranslateContent ? prevTranslateContent + '\n' + value : value,
              };
            });
          }
        } else {
          acc.nonTextAnswers.push(answer);
        }
        return acc;
      },
      {
        nonTextAnswers: [],
        textAnswer: {
          message: undefined,
          messageId: undefined,
          translateMap: undefined,
          msgType: 'TEXT',
        },
      } as {
        nonTextAnswers: SupplementKnowledgeAnswer[];
        textAnswer: {
          message?: string;
          messageId?: string;
          translateMap?: Record<string, string>;
          msgType: 'TEXT';
        };
      },
    );

    return [result.textAnswer, ...result.nonTextAnswers];
  };

  const hasRewriteQuestionDraftChanges = hasRewriteQuestionDraft(rewriteQuestionPayload || {});
  const submitEnable =
    hasRewriteQuestionDraftChanges ||
    (inputValue?.trim() && inputValue !== aTranslate) ||
    (fileAnswers.length > 0 &&
      !isEqual(
        fileAnswers,
        answers.filter((_) => _.msgType !== 'TEXT'),
      ));

  useEffect(() => {
    if (supplementalKnowledgeValue) {
      if (
        supplementalKnowledgeValue.rewriteAnswers &&
        supplementalKnowledgeValue.rewriteAnswers.length > 0
      ) {
        setAnswers(mergeAnswers(supplementalKnowledgeValue.rewriteAnswers));
      } else if (
        supplementalKnowledgeValue.originAnswers &&
        supplementalKnowledgeValue.originAnswers.length > 0
      ) {
        setAnswers(mergeAnswers(supplementalKnowledgeValue.originAnswers));
      } else {
        setAnswers(supplementalKnowledgeValue.answers);
      }
      setEditKey(Date.now());
    }

    return () => {
      setEditKey(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplementalKnowledgeValue]);

  return (
    <>
      {editKey && (
        <PureAnswerEditor
          key={editKey}
          region={region}
          channel={channel}
          answers={answers}
          storeId={storeId}
          onAnswersChange={(answers) => {
            if (!answers[0].messageId) {
              answers[0] = {
                ...answer,
                message: answers[0].message,
              };
            }
            setAnswers(answers);
          }}
        />
      )}
      <div className="absolute bottom-0 left-0 right-0 w-full h-[56px] px-4 bg-[var(--fett-bg-elevated)] border-t border-solid border-[var(--fett-border-default)] shadow flex items-center justify-end gap-4">
        <Button
          type="primary"
          loading={isLoading}
          onClick={handleSaveClick}
          disabled={!submitEnable}
        >
          {enable ? t('保存') : t('保存并启用')}
        </Button>
      </div>
    </>
  );
};
